// The blow: whether a capture finishes its target, and where the shove goes.
//
// §27. A capture kills when the attacker's damage meets the target's health;
// short of that the target loses health and is **shoved** a square along the
// blow, and the attacker takes the square it left. Where the shove has nowhere
// to go the target stands its ground and the attacker stops short of it —
// unless nowhere is the void, which destroys it (ring out, §31 K4).
//
// **Its own file because of an import edge.** `moveGen` has to ask these
// questions — whether a capture is a capture decides which moves exist — and
// `reduce.ts` already imports `canBeHurt` *from* `moveGen`, so answering them
// there would close a cycle. What they read (`healthOf`, `blowOf`) lives in
// `position.ts` for the same reason `armourLeft` does.

import { KING, fileOf, pieceColor, pieceType, rankOf, type Side, STRIDE } from "../board";
import { bashOnto, bashStructure, FLAG_RING_OUT, type Move, moveOnto, moveOntoStructure, NO_BREAK } from "../move";
import { breaksOnLanding, crushes, extend, plowed } from "./plow";
import {
  blowAgainst,
  blowOf,
  crownedFrom,
  facingAt,
  hurtAt,
  imitatingAt,
  moveKing,
  setCrowned,
  setDisguise,
  setFacing,
  setHurt,
  setImitating,
  setPiece,
  setPlating,
  setPieceId,
  pieceIdAt,
  setVariant,
  setVitality,
  platingAt,
  variantAt,
  vitalityAt,
  hasCell,
  healthOf,
  moatLeft,
  obstructsAt,
  pieceAt,
  type Position,
  structureHealthAt
} from "../position";

/**
 * Does this blow finish what it lands on?
 *
 * **An en passant is lethal whatever the target's health**, and that is a
 * carve-out rather than luck. It is pawn takes pawn on every board the tables
 * decide — but a per-square `vitality` can make a pawn tough, and then the
 * blow has *no direction*: the victim is not on the square the capturer lands
 * on, so `to - from` points at the en passant square and not at the pawn. The
 * alternatives are both worse — deriving a direction from the victim's own
 * push is a second geometry to keep in step, and refusing the capture takes a
 * chess move off the board.
 *
 * Unreachable on any board the game ships (`pawnStartRank` gives a camp no
 * home rank, so no double step and no en passant), which is exactly why it
 * would ship broken without being written down.
 */
export function blowKills(
  pos: Position,
  from: number,
  to: number,
  enPassant = false,
  /** Structure HP plowed on the way (§31 S1), off the blow. */
  spent = 0
): boolean {
  if (enPassant) return true;
  // Less the victim's Deflector Shields (§31 A9) — `blowAgainst`.
  return blowAgainst(pos, from, to) - spent >= healthOf(pos, to) || ringsOut(pos, from, to);
}

/**
 * Would this blow's shove land in the **void** (§31 K4) — off `cells`? Then a
 * target the blow did not finish is destroyed instead: a ring out. A
 * building stands on a cell (§31 S1), so an obstruction refuses the shove and
 * anything else is broken by it; a moat or an occupied square refuses it.
 */
export function ringsOut(pos: Position, from: number, to: number): boolean {
  return !hasCell(pos, to + blowStep(from, to));
}

/**
 * The direction a blow travels, as a square offset.
 *
 * `(sign(df), sign(dr))`. For a slider, a king or a pawn this **is** the ray
 * it came down. For a knight's L — `(±2, ±1)` or `(±1, ±2)` — it is still one
 * of the eight compass directions and never `(0,0)`, so the rule is total and
 * needs no special case for the one piece that does not travel in a line.
 */
export function blowStep(from: number, to: number): number {
  const df = Math.sign(fileOf(to) - fileOf(from));
  const dr = Math.sign(rankOf(to) - rankOf(from));
  return dr * STRIDE + df;
}

/**
 * Is `sq` somewhere a piece moved without a move could stand?
 *
 * Exported for the `lance` step, the sixth such door (§26 C13 counted five):
 * one body, or the copy goes stale the day a seventh wall is added.
 */
export function standable(pos: Position, sq: number): boolean {
  // A moat square is a cell and is not standable — a wall (§26 C13). So is an
  // obstruction's (§31 S1); any other building is broken by whoever lands.
  return (
    hasCell(pos, sq) && pieceAt(pos, sq) === 0 && moatLeft(pos, sq) === 0 && !obstructsAt(pos, sq)
  );
}

/**
 * Where the target of this blow is driven, or -1 when it stands its ground.
 *
 * One square further along the same blow. It must be a cell nobody is standing
 * on.
 */
export function shoveSquare(pos: Position, from: number, to: number): number {
  const step = blowStep(from, to);
  const landing = to + step;
  if (!standable(pos, landing)) return -1;
  return landing;
}

/**
 * Where the attacker stops when the shove was refused.
 *
 * It stops at the last square of its approach — adjacent to the target, on the
 * side it came from — which for a slider is a square it has just travelled
 * and so is empty by construction.
 *
 * **A knight has no approach, so it does not move at all.** Its L is not a
 * ray, so "one square back along the blow" is not somewhere it came from and
 * landing there would be a hop it could never otherwise make. The ray test is
 * the three cases a line can be: a file, a rank, or a diagonal.
 *
 * The walk back is the backoff the design asks for and is **unreachable under
 * this geometry** — a slider's approach is empty. It is here so the rule is
 * total rather than true by luck, and it terminates because `from` is on the
 * ray whenever the loop is entered at all.
 */
export function bounceSquare(pos: Position, from: number, to: number): number {
  const df = fileOf(to) - fileOf(from);
  const dr = rankOf(to) - rankOf(from);
  const rayed = df === 0 || dr === 0 || Math.abs(df) === Math.abs(dr);
  if (!rayed) return from;

  const step = blowStep(from, to);
  for (let sq = to - step; sq !== from; sq -= step) {
    if (standable(pos, sq)) return sq;
  }
  // Its own square, which it was leaving anyway: the attacker stays put.
  return from;
}

/**
 * The move a capture actually is — the ordinary one, or a bash.
 *
 * **One home, called from every capture site**, for the reason `canTake` has
 * one: a rule restated at seven sites is a rule that drifts. `canTake` is
 * asked first and is unchanged — it answers whether the target may be taken at
 * all (alliance, armour, the last king's exposure) — and this
 * answers what happens when it is.
 *
 * `promotion` is only ever passed by a lethal blow. A bash does not reach the
 * target's square, so it cannot promote, and the caller emits **one** move
 * rather than the four promotion variants.
 */
export function blowOnto(
  pos: Position,
  from: number,
  to: number,
  target: number,
  promotion = 0,
  /** Structure HP plowed before `to` (§31 S1): comes off the blow. */
  spent = 0,
  /** The structures that plow destroyed, in travel order. */
  path: readonly number[] = NO_BREAK
): Move {
  // Whoever takes `to` crushes an enemy's non-obstruction structure there —
  // the one a target was standing on. Free of the budget: the blow has landed.
  const us = pieceColor(pieceAt(pos, from));
  const landed = pos.structures.size > 0 && crushes(pos, to, us) ? extend(path, to) : path;
  if (blowAgainst(pos, from, to) - spent >= healthOf(pos, to)) {
    return plowed(moveOnto(from, to, target, promotion as 0), landed, spent);
  }
  // Ring out (§31 K4): a capture in every respect, the attacker on `to`.
  if (ringsOut(pos, from, to)) {
    const move = moveOnto(from, to, target, promotion as 0);
    move.flags |= FLAG_RING_OUT;
    return plowed(move, landed, spent);
  }
  const shove = shoveSquare(pos, from, to);
  // Shove lands: the target is driven on and the attacker takes its square —
  // and the target breaks whatever non-obstruction structure it lands on.
  // Shove refused: the target stands and the attacker stops short of it.
  if (shove >= 0) {
    const broken = breaksOnLanding(pos, shove) ? extend(landed, shove) : landed;
    return plowed(bashOnto(from, to, target, to), broken, spent);
  }
  return plowed(bashOnto(from, bounceSquare(pos, from, to), target, to), path, spent);
}

/**
 * The move a blow at a **building** actually is (§27 + §26 F5).
 *
 * `blowOnto`'s twin, and separate for the reason `bashStructure` is: there is
 * no piece on the square, so nothing here can read a target's type or colour,
 * and a building is never driven anywhere — it is masonry.
 *
 * So there are two outcomes rather than three. The blow finishes it and the
 * capturer takes the square, promotions included; or it does not, and the
 * attacker stops where a refused shove would leave it — `bounceSquare`, which
 * answers its own square when it was already standing beside the wall.
 *
 * `promotion` is only ever passed by a lethal blow, `blowOnto`'s rule: a bash
 * does not reach the square, and a piece that did not arrive did not arrive.
 */
export function blowOntoStructure(
  pos: Position,
  from: number,
  to: number,
  owner: Side,
  promotion = 0,
  /** Plow spent reaching it (§31 S1) — example 4's wall behind a belt. */
  spent = 0,
  path: readonly number[] = NO_BREAK
): Move {
  if (blowOf(pos, from) - spent >= structureHealthAt(pos, to)) {
    return plowed(moveOntoStructure(from, to, owner, promotion as 0), path, spent);
  }
  return plowed(bashStructure(from, bounceSquare(pos, from, to), to, owner), path, spent);
}

/**
 * Drive a piece one square, taking everything that belongs to it.
 *
 * For the callers that shove a piece **outside** `makeMove` — a lance's step
 * and a clasp's drag, which are not ordinary moves. `makeMove`
 * writes the same fields from its `Undo` instead, because it has to restore
 * them exactly; here the board is still the truth about them.
 */
export function shovePiece(pos: Position, from: number, to: number): void {
  const piece = pieceAt(pos, from);
  setPiece(pos, to, piece);
  setPiece(pos, from, 0);
  setFacing(pos, to, facingAt(pos, from));
  setFacing(pos, from, 0);
  setHurt(pos, to, hurtAt(pos, from));
  setHurt(pos, from, 0);
  setVitality(pos, to, vitalityAt(pos, from));
  setVitality(pos, from, 0);
  // Armor is worn, so it goes with the body — the pool is moved, not taken off.
  setPlating(pos, to, platingAt(pos, from));
  setPlating(pos, from, 0);
  // Still the same body, so still the same name.
  setPieceId(pos, to, pieceIdAt(pos, from));
  setPieceId(pos, from, 0);
  // A variant is what the piece is, so being shoved cannot change it.
  setVariant(pos, to, variantAt(pos, from));
  setVariant(pos, from, 0);
  setImitating(pos, to, imitatingAt(pos, from));
  setImitating(pos, from, 0);
  setCrowned(pos, to, crownedFrom(pos, from));
  setCrowned(pos, from, 0);
  // And the costume, or a spy shoved or dragged arrives with no cover (§26
  // C12). The raw entry: this is a move, and an expired one is the sweep's.
  setDisguise(pos, to, pos.disguise.get(from) ?? null);
  setDisguise(pos, from, null);
  // The royal list is by square, so a shoved spare has to be told. A *last*
  // king cannot be hurt at all, so it is never shoved.
  if (pieceType(piece) === KING) moveKing(pos, pieceColor(piece), from, to);
}
